<template>
  <input
    v-if="type === 'submit' && !loadingSpinner"
    id="submit-button"
    :class="combinedClass"
    class="font-normal"
    type="submit"
    :value="value"
    :disabled="disabled"
    :autofocus="autoFocus"
  />

  <div
    v-if="loadingSpinner"
    :class="[combinedClass]"
    style="position: relative; display: inline-table"
  >
    <svg
      :class="`loading-${variant}`"
      class="loading relative h-5 w-full"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M28 16C28 18.3734 27.2962 20.6935 25.9776 22.6668C24.6591 24.6402 22.7849 26.1783 20.5922 27.0866C18.3995 27.9948 15.9867 28.2324 13.6589 27.7694C11.3311 27.3064 9.19295 26.1635 7.51472 24.4853C5.83649 22.8071 4.6936 20.6689 4.23058 18.3411C3.76755 16.0133 4.00519 13.6005 4.91345 11.4078C5.8217 9.21509 7.35977 7.34094 9.33316 6.02236C11.3065 4.70379 13.6266 4 16 4"
        stroke="currentColor"
        stroke-width="3"
      />
      <path
        d="M16 1V7L20 4L16 1Z"
        fill="currentColor"
      />
      <path
        d="M22.4895 7.161C24.4444 8.50678 25.9564 10.402 26.8342 12.6071"
        stroke="currentColor"
        stroke-width="3"
      />
    </svg>
  </div>
  <button
    v-if="type !== 'submit' && !loadingSpinner"
    id="button-main"
    :class="combinedClass"
    class="font-normal"
    :disabled="disabled || isLoading"
    :type="type"
    :style="[
      customBg
        ? `background-color: ${customBg}; border: 1px solid ${customBg}`
        : '',
      style,
    ]"
    @keyup.enter="$emit('enter', $event.target.value)"
    @click="clickMe"
  >
    <slot v-if="!isLoading" />
    <svg
      v-else
      :class="`loading-${variant}`"
      class="-top-2px loading relative h-5 w-full"
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M28 16C28 18.3734 27.2962 20.6935 25.9776 22.6668C24.6591 24.6402 22.7849 26.1783 20.5922 27.0866C18.3995 27.9948 15.9867 28.2324 13.6589 27.7694C11.3311 27.3064 9.19295 26.1635 7.51472 24.4853C5.83649 22.8071 4.6936 20.6689 4.23058 18.3411C3.76755 16.0133 4.00519 13.6005 4.91345 11.4078C5.8217 9.21509 7.35977 7.34094 9.33316 6.02236C11.3065 4.70379 13.6266 4 16 4"
        stroke="currentColor"
        stroke-width="3"
      />
      <path
        d="M16 1V7L20 4L16 1Z"
        fill="#fff"
      />
      <path
        d="M22.4895 7.161C24.4444 8.50678 25.9564 10.402 26.8342 12.6071"
        stroke="currentColor"
        stroke-width="3"
      />
    </svg>
  </button>
</template>

<script>
export default {
  name: 'ButtonMain',
  props: {
    variant: {
      type: String,
      default: 'primary',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    type: {
      type: String,
      default: 'button',
    },
    value: String,
    class: String,
    size: String,
    style: Object,
    autoFocus: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
    loadingSpinner: {
      type: Boolean,
      default: false,
    },
    customBg: {
      type: String,
      default: '',
      required: false,
    },
  },
  emits: ['click', 'enter'],
  computed: {
    buttonSize() {
      switch (this.size) {
        case 'xs':
          return 'btn-size-xs';
        case 'sm':
          return 'btn-size-sm';
        case 'md':
          return 'btn-size-md';
        case '2md':
          return 'btn-size-2md';
        case 'custome':
          return '';
        default:
          return 'btn-size-lg';
      }
    },
    combinedClass() {
      const loadingClass =
        this.isLoading || this.loadingSpinner ? 'loading-btn' : '';
      return (
        this.variant +
        ' ' +
        this.class +
        ' ' +
        this.buttonSize +
        ' ' +
        loadingClass
      );
    },
  },
  methods: {
    clickMe(event) {
      this.$emit('click');
      event.target.blur();
    },
  },
};
</script>
<style scoped>
.tertiary {
  @apply w-full cursor-pointer rounded-md bg-off-white text-gray-over-dark;
}

.tertiary:hover {
  @apply bg-hovered-gray !important;
}

.tertiary:focus {
  @apply bg-pressed-gray !important;
}

div.loading-btn,
button:not(.link) {
  height: 53px;
}

button:disabled,
input:disabled {
  @apply cursor-not-allowed border-light-gray bg-light-gray !important;
}
button.link:disabled {
  @apply cursor-not-allowed border-0 bg-transparent !important;
}

.primary {
  @apply w-full cursor-pointer rounded-md border border-yellow-500 bg-yellow-500 text-1md text-white;
}

.primary:hover {
  @apply bg-light-orange !important;
}

.primary:focus {
  @apply bg-dark-orange !important;
}

.primary-bold {
  @apply w-full cursor-pointer rounded-md border border-neutral-subtle-yellow bg-neutral-subtle-yellow text-1md text-white;
}

.primary-bold:hover {
  @apply bg-light-orange !important;
}

.primary-bold:focus {
  @apply bg-dark-orange !important;
}

button.primary:disabled,
input.primary:disabled {
  @apply border-light-gray bg-light-gray !important;
}

.secondary {
  @apply w-full cursor-pointer rounded-md border border-gray-over-dark bg-white text-1md text-gray-over-dark;
}

.secondary:hover {
  @apply border-body-color text-body-color !important;
}

.secondary:focus {
  /* @apply border-dark-gray text-dark-gray !important; */
  @apply border-gray-over-dark text-gray-over-dark !important;
}

.secondary-over-dark {
  @apply w-full cursor-pointer rounded-md border border-gray-over-dark bg-transparent text-1md text-gray-over-dark;
}

.secondary-over-dark:hover {
  @apply border-white text-white !important;
}

.secondary-over-dark:focus {
  @apply border-light-gray text-light-gray !important;
}

button.loading-btn:disabled,
div.loading-btn,
div.loading-btn:hover {
  @apply cursor-default border border-yellow-500 bg-yellow-500 !important;
}

button.secondary:disabled {
  @apply border-light-gray bg-white text-light-gray !important;
}

.quaternary {
  @apply w-full cursor-pointer rounded-md bg-body-color text-white;
}
.quaternary:hover {
  @apply w-full cursor-pointer rounded-md bg-dark-gray text-white;
}
.quaternary:active {
  @apply w-full cursor-pointer rounded-md bg-dark-blue text-white;
}

.warning {
  @apply w-full cursor-pointer rounded-md bg-red-600 text-white;
}
.warning:hover {
  @apply w-full cursor-pointer rounded-md bg-red-700 text-white;
}
.warning:active {
  @apply w-full cursor-pointer rounded-md bg-red-800 text-white;
}

.custom {
  @apply w-full cursor-pointer rounded-md text-1md;
}

.btn-size-xs {
  @apply w-auto p-1 text-xxs !important;
  height: max-content !important;
}

.btn-size-sm {
  @apply w-auto p-2 text-xs !important;
  height: max-content !important;
}

.btn-size-md {
  @apply w-auto p-2 !important;
  height: max-content !important;
}

.btn-size-2md {
  @apply w-auto !important;
  padding: 15px 14px;
  height: max-content !important;
}

.btn-size-lg {
  padding: 15px 24px;
  @apply text-1md !important;
}

.loading-primary {
  @apply text-white;
}

.loading-secondary {
  @apply text-light-gray;
}

.loading-quaternary {
  @apply text-white;
}

.link {
  @apply cursor-pointer p-0 !important;
}

button.custom {
  @apply w-full cursor-pointer rounded-md text-1md text-white;
}

button.custom:hover {
  filter: brightness(0.9);
}
</style>
